'use client'

import { useState } from 'react'
import {
  useStmCapacityExceptions,
  useStmDeleteBlock,
  useStmDeleteCapacityException,
} from '@/features/stm/hooks/use-stm-groups-blocks'
import { BlockFormDialog } from '@/features/stm/components/forms/BlockFormDialog'
import { CapacityExceptionFormDialog } from '@/features/stm/components/forms/CapacityExceptionFormDialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { formatDateRange } from '@/features/stm/lib/date-utils'
import { Plus, Edit2, Trash2, Ban, AlertTriangle, Settings2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { Block, CapacityException } from '@/features/stm/lib/types'

interface BlocksManagementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isAdmin: boolean
  regionId: number | undefined | null
}

/** Convert capacity exception to Block shape for BlockFormDialog when capacity=0 */
function toBlock(e: CapacityException): Block {
  return {
    id: e.id,
    start_date: e.start_date,
    end_date: e.end_date,
    reason: e.reason ?? '',
    severity: (e.severity ?? 'Hard Block') as Block['severity'],
    created_at: e.created_at ?? '',
    updated_at: e.updated_at ?? '',
  }
}

export function BlocksManagementDialog({
  open,
  onOpenChange,
  isAdmin,
  regionId,
}: BlocksManagementDialogProps) {
  const { data: exceptions, isLoading } = useStmCapacityExceptions(regionId ?? null)
  const deleteBlock = useStmDeleteBlock()
  const deleteException = useStmDeleteCapacityException()
  const [blockFormOpen, setBlockFormOpen] = useState(false)
  const [exceptionFormOpen, setExceptionFormOpen] = useState(false)
  const [editingBlock, setEditingBlock] = useState<Block | null>(null)
  const [editingException, setEditingException] = useState<CapacityException | null>(null)

  const blocks = (exceptions || []).filter((e) => e.capacity === 0)
  const capacityOverrides = (exceptions || []).filter((e) => e.capacity > 0)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div>
              <DialogTitle>Manage Blocks & Capacity</DialogTitle>
              <DialogDescription>
                Manage base unavailability (blocks) and capacity overrides for specific weeks
              </DialogDescription>
            </div>
          </DialogHeader>

          <Tabs defaultValue="blocks" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="blocks">
                Blocks ({blocks.length})
              </TabsTrigger>
              <TabsTrigger value="capacity">
                Capacity Exceptions ({capacityOverrides.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="blocks" className="mt-4 space-y-3">
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingBlock(null)
                    setBlockFormOpen(true)
                  }}
                  className="mb-2"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Block
                </Button>
              )}
              {isLoading ? (
                <p className="text-muted-foreground text-center py-8">Loading...</p>
              ) : blocks.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No blocks configured</p>
              ) : (
                blocks.map((e) => {
                  const b = toBlock(e)
                  return (
                    <div
                      key={b.id}
                      className={cn(
                        'flex items-center justify-between p-4 rounded-lg border',
                        b.severity === 'Hard Block'
                          ? 'bg-destructive/5 border-destructive/20'
                          : 'bg-amber-500/5 border-amber-500/20'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {b.severity === 'Hard Block' ? (
                          <Ban className="h-5 w-5 text-destructive" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{b.reason || 'Block'}</span>
                            <Badge variant={b.severity === 'Hard Block' ? 'destructive' : 'secondary'}>
                              {b.severity}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{formatDateRange(b.start_date, b.end_date)}</p>
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingBlock(b)
                              setBlockFormOpen(true)
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => deleteBlock.mutate(b.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </TabsContent>

            <TabsContent value="capacity" className="mt-4 space-y-3">
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingException(null)
                    setExceptionFormOpen(true)
                  }}
                  className="mb-2"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Capacity Exception
                </Button>
              )}
              {isLoading ? (
                <p className="text-muted-foreground text-center py-8">Loading...</p>
              ) : capacityOverrides.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No capacity exceptions configured</p>
              ) : (
                capacityOverrides.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-primary/5 border-primary/20"
                  >
                    <div className="flex items-center gap-3">
                      <Settings2 className="h-5 w-5 text-primary" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">Capacity: {e.capacity}</span>
                          {e.reason && (
                            <span className="text-sm text-muted-foreground">— {e.reason}</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{formatDateRange(e.start_date, e.end_date)}</p>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingException(e)
                            setExceptionFormOpen(true)
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => deleteException.mutate(e.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <BlockFormDialog
        open={blockFormOpen}
        onOpenChange={setBlockFormOpen}
        block={editingBlock}
        regionId={regionId}
      />
      <CapacityExceptionFormDialog
        open={exceptionFormOpen}
        onOpenChange={setExceptionFormOpen}
        exception={editingException}
        regionId={regionId}
      />
    </>
  )
}
